import { Combobox } from '@kalos/ui';
import { type FC, useMemo } from 'react';

import {
  useRoleCategoriesByRank,
  useRoleListQuery,
  useRolesForPicker,
} from '../../../hooks/react-query/useOrgStructureQuery';

/**
 * Pick a job title from the managed list (ClickUp 86bbfvuyb).
 *
 * Replaces free-text job titles, which is why the same job currently exists
 * twice under different spellings. Searchable rather than a plain dropdown:
 * there are enough titles that scrolling for one is slower than typing it.
 *
 * Titles matching the employee's department sort to the top, but every active
 * title stays selectable — titles legitimately span departments, and filtering
 * would hide valid choices.
 *
 * The level is shown beneath, read-only. A person never carries a level of
 * their own: it comes from the title's category, so correcting a title's rung
 * corrects everyone holding it. Showing it at the moment of picking is what
 * makes the consequence of the choice visible without implying it can be set.
 *
 * Reports the role's *name* alongside its id. During the transition `empTitle`
 * is still what several consumers read, so the caller writes both: the id
 * starts populating the new column while the text keeps what exists working.
 * Once nothing reads empTitle the name can be dropped.
 *
 * Built on the shared Combobox rather than its own Command in a Popover. The
 * hand-rolled one could not be scrolled by wheel or trackpad: a ScrollArea
 * needs a definite height to scroll, not a max height, and the viewport it
 * renders needs the same override Combobox applies. Using the component every
 * other picker on this form uses removes the question.
 */
export const JobTitlePicker: FC<{
  roleId: number;
  /** Current free-text title, shown when it has no matching role yet. */
  fallbackTitle?: string;
  /** Orders suggestions only — every active role stays selectable. */
  departmentId?: number;
  disabled?: boolean;
  onChange: (next: { roleId: number; name: string }) => void;
}> = ({ roleId, fallbackTitle, departmentId, disabled = false, onChange }) => {
  const { roles, isPending } = useRolesForPicker({ departmentId });
  const { categories } = useRoleCategoriesByRank();
  // Every title, archived ones included. `roles` is the active list — what this
  // offers — but a person can hold a title that was archived after they were
  // given it, and looking the current one up in the offer list would not find
  // it. That read as "not on the managed list" and fell back to the stale
  // free-text copy, so a renamed-then-archived title showed its old name here
  // while the directory showed the new one.
  const { data: everyRole } = useRoleListQuery();

  const selected = (everyRole?.results ?? []).find((r) => r.id === roleId);
  const level = selected ? categories.find((c) => c.id === selected.roleCategoryId) : undefined;
  const archived = !!selected && !selected.isActive;
  // Only when there is no title to resolve at all. Someone holding a role has a
  // name on the managed list, whatever `emp_title` still says.
  const unmatched = !roleId && !!fallbackTitle?.trim();

  const values = useMemo(() => {
    const options = roles.map((r) => ({ value: String(r.id), label: r.name }));
    // An archived title is not offered, but the person holding it needs to see
    // which one it is rather than an empty control.
    if (selected && !roles.some((r) => r.id === selected.id)) {
      options.unshift({ value: String(selected.id), label: `${selected.name} (archived)` });
    }
    return options;
  }, [roles, selected]);

  return (
    <div>
      <Combobox
        values={values}
        value={roleId ? String(roleId) : ''}
        onChange={(next) => {
          const role = (everyRole?.results ?? []).find((r) => String(r.id) === next);
          if (role) onChange({ roleId: role.id, name: role.name });
        }}
        disabled={disabled}
        isLoading={isPending}
        // Below the field, like every other picker on this form. Radix picks
        // the side with room by default, which put a long list above it.
        side="bottom"
        placeholder="Search job titles…"
        emptyLabel={fallbackTitle?.trim() || 'Select a title'}
        emptySearch={roles.length === 0 ? 'No job titles have been added yet.' : 'No matches.'}
        triggerClassName="mt-1 h-10 w-full"
      />

      {level ? (
        <p className="mt-1 text-xs text-neutral-600">
          Level {level.sortOrder} · {level.name}
        </p>
      ) : null}

      {unmatched ? (
        <p className="mt-1 text-[11px] text-amber-700">
          Currently &ldquo;{fallbackTitle}&rdquo; — not yet on the managed list. Picking a title
          above replaces it.
        </p>
      ) : null}

      {/* Names the title and says it is managed, because the thing worth
          knowing is that this is a real title in an archived state — not a
          leftover string. The sentence above says the opposite for someone who
          genuinely has no title on the list, and the two must not be confused. */}
      {archived ? (
        <p className="mt-1 text-[11px] text-amber-700">
          &ldquo;{selected?.name}&rdquo; is on the managed list but has been archived. They keep it
          until it is changed; it is no longer offered for anyone else.
        </p>
      ) : null}
    </div>
  );
};
